/**
 * Where a retired address actually lands, written once (AXAI-SEO-002, 22 October 2026).
 *
 * Search Console reported "Page with redirect" against axai.com.au on 22 October 2026. A crawl of
 * the 62 sitemap addresses found all of them answering 200, so the report was not about the
 * sitemap: it was about the site linking its own retired addresses. Six of them were still linked
 * from 38 live pages, and fifteen of the eighteen call-to-action pages the content pipeline links
 * (src/lib/pipeline/next-steps.ts) answered 308. Two addresses redirected twice - /impact and
 * /inclusive-work went to /ai-supported-work, which goes to /#studio.
 *
 * A 308 does not lose a reader and it passes ranking on, so none of this was urgent. It is still
 * worth not doing: Google re-crawls a chain before it follows it, the sitemap and the internal
 * links disagreed with each other, and the pipeline was about to write the same dead link into
 * every new post.
 *
 * The redirects themselves stay in next.config.js, which is the one place that answers a request
 * for a retired address. This module is the other half: where our OWN links should point, so a
 * link never has to be redirected. tests/unit/no-redirect-chains.test.ts holds the two together -
 * it reads next.config.js and fails if anything here is itself a redirect source.
 *
 * TO RESTORE A PAGE: delete its row (or change the constant). Every link follows, because nothing
 * else writes these paths.
 */

/**
 * Where "how AI-supported work works" lives.
 *
 * Restored to a page of its own, 22 October 2026. Annette: "ye we need an ai supported work page -
 * didnt realise it was gone". The rebrand had retired it to the /#studio section of the home page,
 * and it is the single most-linked address aXai has - the standard closing of every editorial post
 * links it, WHY_AXAI links the phrase from every page carrying the core message, and 32 live pages
 * were pointing at the redirect. A homepage fragment could not rank for the phrase or carry its
 * own title, description and schema.
 */
export const AI_SUPPORTED_WORK_PATH = '/ai-supported-work';

/**
 * Each retired call-to-action address and the live address it redirects to, copied from the
 * redirect table in next.config.js and checked against the live site on 22 October 2026.
 *
 * Only addresses our own copy links need to be here.
 *
 * 23 Oct 2026: /what-we-do/ai-for-ndis-providers and /services/ai-consulting-small-business came
 * off this list because they are pages again - Google was serving their old titles as sitelinks
 * and the clicks landed on /what-we-do. ctaHref now returns each of them unchanged, so the
 * approved call-to-action sentences link the page the topic actually names.
 */
export const CTA_DESTINATIONS: Readonly<Record<string, string>> = {
  '/custom-accessibility': '/what-we-do#work',
  '/services/accessibility-audit': '/what-we-do#work',
  '/videos/induction-videos': '/videos',
  '/what-we-do/accessible-content-and-video': '/what-we-do#work',
  '/what-we-do/digital-accessibility': '/what-we-do#work',
  '/what-we-do/ai-for-access-consultants': '/what-we-do#work',
  '/what-we-do/ai-capability-and-vybrid-sessions': '/what-we-do#vybrid',
  '/use-cases/easy-read-website': '/axread',
  '/use-cases/policy-explainer-video': '/videos',
  '/ai-for/support-coordinators-and-plan-managers': '/what-we-do#who',
  '/ai-for/disability-employment-services': '/what-we-do#who',
  '/ai-for/sil-and-sda-providers': '/what-we-do#who',
};

/**
 * The live address to link for `path`: its destination if it has been retired, otherwise itself.
 *
 * A path that was never retired passes straight through, so a caller does not have to know which
 * is which.
 */
export function ctaHref(path: string): string {
  return CTA_DESTINATIONS[path] ?? path;
}

/** True when `path` is one of the retired addresses (so our copy should not link it). */
export function isRetiredPath(path: string): boolean {
  return path in CTA_DESTINATIONS;
}
